import { ModalDialog } from './ModalDialog.ts'
import { escape_html } from './html.ts'

export interface FormDialogField {
  id: string
  label: string
  value?: string
  type?: 'text' | 'number'
  step?: string
  min?: string
  max?: string
}

export interface FormDialogOptions {
  title: string
  fields: FormDialogField[]
  submit_label?: string
  cancel_label?: string
}

export function prompt_text (options: {
  title: string
  label: string
  value?: string
  submit_label?: string
  cancel_label?: string
}): Promise<string | null> {
  return prompt_fields({
    title: options.title,
    submit_label: options.submit_label,
    cancel_label: options.cancel_label,
    fields: [{ id: 'value', label: options.label, value: options.value }]
  }).then(values => values?.value ?? null)
}

export function prompt_fields (options: FormDialogOptions): Promise<Record<string, string> | null> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (value: Record<string, string> | null, dialog: ModalDialog): void => {
      settled = true
      dialog.remove()
      resolve(value)
    }
    const dialog = new ModalDialog(
      options.title,
      build_form_dialog_html(options),
      {
        customClass: 'form-dialog',
        onClose: () => {
          if (!settled) {
            settled = true
            resolve(null)
          }
        }
      }
    )
    dialog.show()
    const root = dialog.get_element()
    const form = root?.querySelector<HTMLFormElement>('[data-form-dialog="form"]')
    form?.addEventListener('submit', (event) => {
      event.preventDefault()
      const values: Record<string, string> = {}
      for (const field of options.fields) {
        values[field.id] = form.querySelector<HTMLInputElement>(`[name="${css_escape(field.id)}"]`)?.value ?? ''
      }
      finish(values, dialog)
    })
    root?.querySelector<HTMLButtonElement>('[data-form-dialog="cancel"]')
      ?.addEventListener('click', () => finish(null, dialog))
    const first_input = root?.querySelector<HTMLInputElement>('input')
    first_input?.focus()
    first_input?.select()
  })
}

export function build_form_dialog_html (options: FormDialogOptions): string {
  const submit_label = options.submit_label ?? 'Save'
  const cancel_label = options.cancel_label ?? 'Cancel'
  const fields_html = options.fields.map(field => {
    const type = field.type ?? 'text'
    const attrs = [
      `type="${type}"`,
      `id="form-dialog-${escape_attr(field.id)}"`,
      `name="${escape_attr(field.id)}"`,
      `value="${escape_attr(field.value ?? '')}"`,
      field.step !== undefined ? `step="${escape_attr(field.step)}"` : '',
      field.min !== undefined ? `min="${escape_attr(field.min)}"` : '',
      field.max !== undefined ? `max="${escape_attr(field.max)}"` : ''
    ].filter(Boolean).join(' ')
    return `
      <label class="form-dialog-field" for="form-dialog-${escape_attr(field.id)}">
        <span>${escape_html(field.label)}</span>
        <input ${attrs}>
      </label>
    `
  }).join('')

  return `
    <form class="form-dialog-body" data-form-dialog="form">
      ${fields_html}
      <div class="form-dialog-actions">
        <button type="button" class="secondary-button" data-form-dialog="cancel">${escape_html(cancel_label)}</button>
        <button type="submit">${escape_html(submit_label)}</button>
      </div>
    </form>
  `
}

function css_escape (value: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value.replaceAll('"', '\\"')
}


function escape_attr (value: string): string {
  return escape_html(value)
}
